import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  MenuItem,
  ListItemText,
  Box,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
} from "@mui/material";
import { getDepartmentsByLocation, getEmployeeMonthlyData } from "src/api/faceRegistration";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import dayjs from "dayjs";
import { getURL } from "src/utils/config";
import axios from "axios";
import Page from "src/components/Page";
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';

const EMPTY_CELL_VALUE = "N/A";
const OUTLINED_BORDER_COLOR = "rgba(0, 0, 0, 0.23)";
const FILTER_CONTROL_WIDTH = 220;
const INITIAL_VISIBLE_ROWS = 20;
const ROW_LOAD_BATCH_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 80;

const EMPLOYEE_NAME_FIELDS = [
  "EmployeeName",
  "employeeName",
  "Name",
  "name",
  "employee_name",
  "UserName",
  "userName"
];

const LOCATION_FIELDS = [
  "LocationName",
  "locationName",
  "Location",
  "location",
  "location_name"
];

const DEPARTMENT_NAME_FIELDS = [
  "DepartmentName",
  "departmentName",
  "Department",
  "department",
  "department_name"
];

const ABSENT_DAY_DETAIL_FIELDS = [
  "AbsentDayDetails",
  "absentDayDetails",
  "absent_day_details",
  "AbsentDaysDetails",
  "absentDaysDetails",
  "absent_days_details",
  "AbsentDetails",
  "absentDetails",
  "absent_details",
  "AbsentDates",
  "absentDates",
  "absent_dates",
  "AbsentDateList",
  "absentDateList",
  "absent_date_list",
  "LeaveDetails",
  "leaveDetails",
  "leave_details",
  "LeaveDates",
  "leaveDates",
  "leave_dates",
  "Leaves",
  "leaves",
  "AbsentDays",
  "absentDays",
  "absent_days"
];

const ABSENT_DATE_FIELDS = [
  "date",
  "Date",
  "attendance_date",
  "AttendanceDate",
  "absent_date",
  "AbsentDate",
  "leave_date",
  "LeaveDate"
];

const ABSENT_LEAVE_TYPE_FIELDS = [
  "leave_type",
  "leaveType",
  "LeaveType",
  "Leave_Type",
  "type",
  "Type",
  "status",
  "Status"
];

const normalizeDepartmentOptions = (departments) =>
  (Array.isArray(departments) ? departments : (departments ? [departments] : []))
    .map((departmentItem, index) => ({
      ...departmentItem,
      Id: departmentItem?.Id ??
        departmentItem?.ID ??
        departmentItem?.id ??
        departmentItem?.DepartmentId ??
        departmentItem?.DepartmentID ??
        departmentItem?.department_id ??
        `department-${index}`,
      DepartmentName: String(
        departmentItem?.DepartmentName ??
        departmentItem?.departmentName ??
        departmentItem?.Department ??
        departmentItem?.department ??
        departmentItem?.Name ??
        departmentItem?.name ??
        ''
      ).trim(),
    }))
    .filter((departmentItem) => departmentItem.DepartmentName);

const attendanceTableColumns = [
  {
    id: "employeeName",
    label: "Employee Name",
    width: 20,
    getValue: (item) => getFirstFieldValue(item, EMPLOYEE_NAME_FIELDS)
  },
  {
    id: "location",
    label: "Location",
    width: 15,
    getValue: (item) => getFirstFieldValue(item, LOCATION_FIELDS)
  },
  {
  id: "month",
  label: "Month",
  width: 15,
  getValue: (item) => getFirstFieldValue(item, [
    "Month",
    "month",
    "MonthName",
    "monthName",
    "month_name",
    "AttendanceMonth",
    "attendanceMonth"
    ])
  },
  {
    id: "departmentName",
    label: "Department Name",
    width: 20,
    getValue: (item) => getFirstFieldValue(item, DEPARTMENT_NAME_FIELDS)
  },
  {
    id: "presentDays",
    label: "Present Days",
    width: 15,
    getValue: (item) => getFirstFieldValue(item, [
      "PresentDays",
      "presentDays",
      "present_days",
      "Present",
      "present",
      "PresentCount",
      "presentCount"
    ])
  },
  {
    id: "absentDays",
    label: "Absent Days",
    width: 15,
    getValue: (item) => {
      const value = getFirstFieldValue(item, [
        "AbsentDays",
        "absentDays",
        "absent_days",
        "Absent",
        "absent",
        "AbsentCount",
        "absentCount"
      ]);
      const parsedRecords = parseArrayValue(value);

      if (Array.isArray(value)) {
        return value.length;
      }

      if (parsedRecords.length > 0) {
        return parsedRecords.length;
      }

      return value;
    }
  },
  {
    id: "attendancePercentage",
    label: "Attendance Percentage",
    width: 20,
    getValue: (item) => formatAttendancePercentage(item)
  }
];

const getFirstFieldValue = (item, fieldNames, fallback = EMPTY_CELL_VALUE) => {
  const value = fieldNames
    .map((fieldName) => item?.[fieldName])
    .find((fieldValue) => (
      fieldValue !== null &&
      fieldValue !== undefined &&
      String(fieldValue).trim() !== ""
    ));

  return value === undefined ? fallback : value;
};

const getNumberFieldValue = (item, fieldNames) => {
  const value = getFirstFieldValue(item, fieldNames, "");
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
};

const formatAttendancePercentage = (item) => {
  const percentage = getFirstFieldValue(
    item,
    [
      "AttendancePercentage",
      "attendancePercentage",
      "attendance_percentage",
      "AttendancePercent",
      "attendancePercent",
      "attendance_percent",
      "Percentage",
      "percentage"
    ],
    ""
  );

  if (percentage !== "") {
    return percentage;
  }

  const totalDays = getNumberFieldValue(item, [
    "TotalDays",
    "totalDays",
    "total_days",
    "Total",
    "total"
  ]);
  const presentDays = getNumberFieldValue(item, [
    "PresentDays",
    "presentDays",
    "present_days"
  ]);

  if (totalDays && presentDays !== null) {
    return `${((presentDays / totalDays) * 100).toFixed(2)}%`;
  }

  return EMPTY_CELL_VALUE;
};

const parseArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string" || value.trim() === "") {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
};

const getAbsentDetailItems = (item) => {
  for (const fieldName of ABSENT_DAY_DETAIL_FIELDS) {
    const records = parseArrayValue(item?.[fieldName]);
    if (records.length > 0) {
      return records;
    }
  }

  return [];
};

const buildAbsentRecords = (item) => {
  const employeeName = getFirstFieldValue(item, EMPLOYEE_NAME_FIELDS);
  const location = getFirstFieldValue(item, LOCATION_FIELDS);
  const departmentName = getFirstFieldValue(item, DEPARTMENT_NAME_FIELDS);

  return getAbsentDetailItems(item)
    .map((detail) => {
      const detailItem = detail && typeof detail === "object" ? detail : { date: detail };
      const date = getFirstFieldValue(detailItem, ABSENT_DATE_FIELDS, "");

      return {
        employeeName,
        location,
        departmentName,
        date: date || EMPTY_CELL_VALUE,
        leaveType: getFirstFieldValue(detailItem, ABSENT_LEAVE_TYPE_FIELDS, "Absent"),
      };
    })
    .filter((record) => record.date !== EMPTY_CELL_VALUE);
};

const getLeaveTypeChipColor = (leaveType) => {
  const normalizedLeaveType = String(leaveType || "").toLowerCase();

  if (normalizedLeaveType.includes("absent") || normalizedLeaveType.includes("sick")) {
    return "error";
  }

  if (normalizedLeaveType.includes("casual")) {
    return "warning";
  }

  return "default";
};

const isClickableAbsentValue = (value) => {
  if (value === EMPTY_CELL_VALUE || value === "" || value === null || value === undefined) {
    return false;
  }

  const numericValue = Number(value);
  return !Number.isFinite(numericValue) || numericValue > 0;
};

const MonthlyAttendance = () => {
  const [filteredData, setFilteredData] = useState([]);
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);
  const [apiData, setApiData] = useState(null);
  const [locationNames, setLocationNames] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [departmentError, setDepartmentError] = useState('');
  const departmentRequestIdRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState(false);
  
  // New state for Month and Year
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState();
  const [monthError, setMonthError] = useState(false);
  const [yearError, setYearError] = useState(false);
  
  // Dialog state
  const [absentDialogOpen, setAbsentDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [absentRecords, setAbsentRecords] = useState([]);
  
  // Generate year options (current, next, next to next)
  const currentYear = dayjs().year();
  const yearOptions = [currentYear, currentYear + 1, currentYear + 2];
  
  // Generate month options
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
  ];

  const handleMonthChange = (value) => {
    setSelectedMonth(value);
    setMonthError(false);
  };

  const handleYearChange = (value) => {
    setSelectedYear(value);
    setYearError(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const apiUrl = getURL("employee-shifts-info/location-names?status=1");
      const AUTH_TOKEN = `${localStorage.getItem('token_type')} ${localStorage.getItem('access_token')}`;
      
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: AUTH_TOKEN,
          },
        });

        const extractedLocationNames = response.data.map(item => item.LocationName);
        setLocationNames(extractedLocationNames);
        setApiData(response.data);
      } catch (error) {
        console.error('Error:', error);
      }
    };
  
    fetchData();
  }, []);

  const updateLocation = useCallback((value) => {
    setSelectedLocation(value);
    const selectedLocationObject = apiData?.find((location) => location.LocationName === value);
    const locationId = selectedLocationObject ? selectedLocationObject.Id : null;
    setSelectedLocationId(locationId);
    if (value) {
      localStorage.setItem("NetLocation", value);
    } else {
      localStorage.removeItem("NetLocation");
    }
    setLocationError(false);
    
    setSelectedDepartments([]);
    setDepartmentOptions([]);
    setDepartmentError('');

    const requestId = departmentRequestIdRef.current + 1;
    departmentRequestIdRef.current = requestId;

    if (!locationId) {
      setDepartmentLoading(false);
      return;
    }

    setDepartmentLoading(true);
    getDepartmentsByLocation({ location_id: locationId }, (response) => {
      if (departmentRequestIdRef.current !== requestId) return;

      if (response.status === 200) {
        setDepartmentOptions(normalizeDepartmentOptions(response.data));
        setDepartmentError('');
      } else {
        setDepartmentOptions([]);
        setDepartmentError('Unable to load departments');
      }
      setDepartmentLoading(false);
    });
  }, [apiData]);

  const handleLocationChange = (value) => {
    updateLocation(value);
  };

  const handleDepartmentChange = (_event, value) => {
    if (!selectedLocation || departmentLoading) {
      setSelectedDepartments([]);
      return;
    }
    if (value.some((option) => option.isSelectAll)) {
      handleSelectAllDepartments();
      return;
    }
    setSelectedDepartments(value);
  };

  const handleSelectAllDepartments = () => {
    if (!departmentOptions.length) {
      setSelectedDepartments([]);
      return;
    }
    if (selectedDepartments.length === departmentOptions.length) {
      setSelectedDepartments([]);
    } else {
      setSelectedDepartments(departmentOptions);
    }
  };

  const handleSubmit = () => {
    const locationId = selectedLocationId || 0;

    setLoading(true);
    setLocationError(false);
    setMonthError(false);
    setYearError(false);

    if (locationId === 0 || !selectedMonth || !selectedYear) {
      if (locationId === 0) {
        setLocationError(true);
      }
      if (!selectedMonth) {
        setMonthError(true);
      }
      if (!selectedYear) {
        setYearError(true);
      }
      setLoading(false);
      return;
    }

    // Get department IDs for the selected departments
    const departmentIds = selectedDepartments.map(dept => dept.Id);

    const data = {
      location_id: locationId,
      month: selectedMonth.value || selectedMonth,
      year: selectedYear,
      department_id: departmentIds.join(','),
    };
    
    console.log('Submitting with data:', data);
    
    getEmployeeMonthlyData(data, (response) => {
      if (response.status === 200) {

        const monthName =
        monthOptions.find(m => m.value === (selectedMonth.value || selectedMonth))?.label || "";

         const updatedData = response.data.map(item => ({
          ...item,
          Month: monthName
        }));

        setFilteredData(updatedData);
        setVisibleRowCount(INITIAL_VISIBLE_ROWS);
      }
      setLoading(false);
    });
  };

  const handleExportExcel = async () => {
    const exportData = filteredData;
    const monthName = monthOptions.find(m => m.value === selectedMonth.value)?.label || selectedMonth;
    const fileName = `Attendance_${monthName}_${selectedYear}.xlsx`;

    if (exportData.length !== 0) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Monthly Attendance");
      
      // Define headers with styles
      worksheet.columns = attendanceTableColumns.map((column) => ({
        header: column.label,
        key: column.id,
        width: column.width,
        alignment: { horizontal: "center" },
      }));

      // Add rows with data
      exportData.forEach((item, index) => {
        const rowData = attendanceTableColumns.reduce((acc, column) => ({
          ...acc,
          [column.id]: column.getValue(item, index)
        }), {});
        const row = worksheet.addRow(rowData);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      // Apply styles to headers
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
    } else {
      alert("No data to export.");
    }
  };

  const handleAbsentDaysClick = (item) => {
    const employeeName = getFirstFieldValue(item, EMPLOYEE_NAME_FIELDS);

    if (!employeeName || employeeName === EMPTY_CELL_VALUE) {
      alert("Employee name not found");
      return;
    }

    setSelectedEmployee(item);
    setAbsentRecords(buildAbsentRecords(item));
    setAbsentDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setAbsentDialogOpen(false);
    setSelectedEmployee(null);
    setAbsentRecords([]);
  };

  const visibleData = filteredData.slice(0, visibleRowCount);

  const handleTableScroll = useCallback((event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= SCROLL_LOAD_THRESHOLD_PX;

    if (!isNearBottom) {
      return;
    }

    setVisibleRowCount((currentCount) => (
      Math.min(currentCount + ROW_LOAD_BATCH_SIZE, filteredData.length)
    ));
  }, [filteredData.length]);

  const filterControlSx = (hasError = false, width = FILTER_CONTROL_WIDTH) => ({
    width,
    minWidth: width,
    '& .MuiOutlinedInput-root': {
      minHeight: 56,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
      '&:hover fieldset': {
        borderColor: hasError ? 'error.main' : 'text.primary',
      },
      '&.Mui-focused fieldset': {
        borderColor: hasError ? 'error.main' : 'primary.main',
      },
      '&.Mui-disabled fieldset': {
        borderColor: hasError ? 'error.main' : OUTLINED_BORDER_COLOR,
      },
    },
    '& .MuiFormHelperText-root': {
      minHeight: 20,
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiInputLabel-root': {
      backgroundColor: 'background.paper',
      px: 0.5,
    },
  });

  return (
    <Page title="Monthly Attendance Report">
      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              flexWrap: 'nowrap',
              gap: 2,
              mx: 2,
              mt: 2,
              mb: 2,
              overflowX: 'auto',
              pt: 1.5,
              pb: 1,
            }}
          >
            {/* Month Dropdown */}
            <FormControl sx={filterControlSx(monthError)}>
              <Autocomplete
                value={selectedMonth}
                onChange={(_event, value) => handleMonthChange(value || null)}
                options={monthOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => option.value === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Month"
                    variant="outlined"
                    error={monthError}
                    helperText={monthError ? "Please select Month" : " "}
                  />
                )}
                sx={filterControlSx(monthError)}
              />
            </FormControl>

            {/* Year Dropdown */}
            <FormControl sx={filterControlSx(yearError)}>
              <Autocomplete
                value={selectedYear}
                onChange={(_event, value) => handleYearChange(value || null)}
                options={yearOptions}
                getOptionLabel={(option) => option?.toString() || ""}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Year"
                    variant="outlined"
                    error={yearError}
                    helperText={yearError ? "Please select Year" : " "}
                  />
                )}
                sx={filterControlSx(yearError)}
              />
            </FormControl>

            {/* Location Dropdown */}
            <FormControl sx={filterControlSx(locationError)}>
              <Autocomplete
                value={selectedLocation}
                onChange={(_event, value) => handleLocationChange(value || "")}
                options={locationNames}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    variant="outlined"
                    error={locationError}
                    helperText={locationError ? "Please Select Location" : " "}
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => option || ""}
                noOptionsText="No locations available"
                //disabled={locationNames.length === 0}
                sx={filterControlSx(locationError)}
              />
            </FormControl>

            {/* Department Dropdown */}
            <FormControl sx={filterControlSx(Boolean(departmentError))}>
              <Autocomplete
                multiple
                limitTags={1}
                renderTags={() => null}
                value={selectedDepartments}
                onChange={handleDepartmentChange}
                options={departmentOptions}
                loading={departmentLoading}
                loadingText="Loading departments..."
                noOptionsText={
                  selectedLocation
                    ? (departmentError || "No departments available")
                    : "Select location first"
                }
                getOptionLabel={(option) => option.DepartmentName || ""}
                isOptionEqualToValue={(option, value) => String(option.Id) === String(value.Id)}
                disabled={!selectedLocation || departmentLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    variant="outlined"
                    error={Boolean(departmentError)}
                    helperText={departmentError || " "}
                    placeholder={
                      departmentLoading
                        ? "Loading departments..."
                        : !selectedLocation
                          ? "Select location first"
                          : "Select departments"
                    }
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <MenuItem {...props}>
                    <Checkbox
                      checked={
                        option.isSelectAll
                          ? departmentOptions.length > 0 && selectedDepartments.length === departmentOptions.length
                          : selected
                      }
                    />
                    <ListItemText primary={option.DepartmentName} />
                  </MenuItem>
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 200,
                  },
                }}
                componentsProps={{
                  popper: {
                    placement: 'bottom-start',
                  },
                }}
                sx={filterControlSx(Boolean(departmentError))}
                filterOptions={(options) => {
                  if (!selectedLocation || departmentLoading || options.length === 0) {
                    return options;
                  }
                  const selectAllOption = {
                    Id: -1,
                    DepartmentName: 'Select All',
                    isSelectAll: true,
                  };
                  return [selectAllOption, ...options];
                }}
              />
            </FormControl>

            {/* Submit and Export Buttons */}
            <Box sx={{ display: 'flex', gap: 1, minHeight: 56, alignItems: 'center', pt: 0 }}>
              <LoadingButton type="submit" variant="contained" onClick={handleSubmit} loading={loading}>
                Submit
              </LoadingButton>

              <LoadingButton type="submit" variant="contained" onClick={handleExportExcel}>
                Export
              </LoadingButton>
            </Box>
          </Box>

          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              onScroll={handleTableScroll}
              sx={{ minWidth: 1100, maxHeight: '65vh', overflow: 'auto' }}
            >
              <Table stickyHeader sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow>
                    {attendanceTableColumns.map((column) => (
                      <TableCell key={column.id}>{column.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleData.map((item, index) => {
                    const absoluteIndex = index;

                    return (
                      <TableRow key={absoluteIndex}>
                        {attendanceTableColumns.map((column) => {
                          const value = column.getValue(item, absoluteIndex);
                          
                          // Make Absent Days clickable
                          if (column.id === "absentDays" && isClickableAbsentValue(value)) {
                            return (
                              <TableCell 
                                key={column.id}
                                sx={{ 
                                  cursor: 'pointer',
                                  color: 'primary.main',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  }
                                }}
                                onClick={() => handleAbsentDaysClick(item)}
                              >
                                {value}
                              </TableCell>
                            );
                          }
                          
                          return (
                            <TableCell key={column.id}>
                              {value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {filteredData.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
              Showing {visibleData.length} of {filteredData.length}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Absent Days Dialog */}
      <Dialog
        open={absentDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Absent Days Details
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedEmployee && (
            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>
              {getFirstFieldValue(selectedEmployee, EMPLOYEE_NAME_FIELDS)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {absentRecords.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Employee Name</strong></TableCell>
                    <TableCell><strong>Location</strong></TableCell>
                    <TableCell><strong>Department Name</strong></TableCell>
                    <TableCell><strong>Leave Type</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {absentRecords.map((record, index) => (
                    <TableRow key={`${record.date}-${index}`}>
                      <TableCell>{record.employeeName}</TableCell>
                      <TableCell>{record.location}</TableCell>
                      <TableCell>{record.departmentName}</TableCell>
                      <TableCell>
                        <Chip 
                          label={record.leaveType} 
                          size="small"
                          color={getLeaveTypeChipColor(record.leaveType)}
                        />
                      </TableCell>
                      <TableCell>{record.date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>No absent dates found for this employee.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default MonthlyAttendance;
